<template>
  <div class="base-container">
    <el-container>
      <el-main :class="{ mb15: showUserActions }" class="shadow pb-none">
        <el-row v-if="showUserActions">
          <Onboarding :step="step" />
        </el-row>
        <template v-else>
          <el-row>
            <el-col>
              <NewsBlock />
            </el-col>
          </el-row>
          <el-row>
            <el-col
              v-for="item in infoItems"
              :key="item.text"
              :md="4"
              :lg="4"
              :xl="4"
              :xs="8"
              :sm="8"
              :gutter="10"
            >
              <InfoBlock
                v-model="item.value"
                :text="item.text"
                :icon="item.icon"
                :loading="loadingInfoBlock"
              />
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="12" :xs="24">
              <el-card class="box-card table">
                <template #header>
                  <div class="clearfix">
                    <WidgetPeridodSelector
                      v-model="period"
                      :disabled="loadingSpendingBlock"
                      @update:modelValue="updatePeriod"
                    />
                  </div>
                </template>
                <ActivityTable
                  :loading="loadingSpendingBlock"
                  :data="spendingActivity"
                />
              </el-card>
            </el-col>
            <el-col :span="12" :xs="24">
              <el-card class="box-card activity">
                <template #header>
                  <div class="clearfix">
                    <span>Spending activity</span>
                  </div>
                </template>
                <ActivityChart
                  :loading="loadingSpendingBlock"
                  :data="spendingActivity"
                />
              </el-card>
            </el-col>
          </el-row>
          <el-card class="box-card main">
            <template #header>
              <div class="clearfix">
                <span style="line-height: 2.5">Your ads activity</span>
                <AdTypeSelect
                  v-model="adType"
                  style="float: right"
                  :with-empty="true"
                  :radio-view="true"
                  :disabled="loadingActivityBlock"
                  :lables="dashboardAdTypeLabels"
                  @update:modelValue="updateAdType"
                />
              </div>
            </template>
            <el-row>
              <el-col :span="24">
                <MainChart
                  :data="fullActivity"
                  :loading="loadingActivityBlock"
                />
              </el-col>
            </el-row>
          </el-card>
        </template>
      </el-main>
    </el-container>
  </div>
</template>

<script lang="ts">
import { computed, ref, defineComponent } from 'vue'
import { useUserStore } from '@/store/modules/user'
import { useWidgetsStore } from '@/apps/traforama/store/widgets'
import { AdType, DspAdTypeLabels } from '@/api/types'
import { FlatDealAdTypeLabels } from '@/apps/traforama/lables'

import { ElMessage } from 'element-plus'
import InfoBlock from './components/InfoBlock.vue'
import ActivityTable from './components/ActivityTable.vue'
import WidgetPeridodSelector from '@/components/WidgetPeridodSelector.vue'
import NewsBlock from '@/components/NewsBlock.vue'
import AdTypeSelect from '@/components/Select/AdTypeSelect.vue'
import MainChart from './components/MainChart.vue'
import ActivityChart from './components/ActivityChart.vue'
import Onboarding from './components/Onboarding.vue'
import { formatMoney } from './utils/formatters'

export default defineComponent({
  name: 'Dashboard',
  components: {
    MainChart,
    InfoBlock,
    ActivityTable,
    ActivityChart,
    WidgetPeridodSelector,
    AdTypeSelect,
    NewsBlock,
    Onboarding
  },
  setup() {
    const widgetsStore = useWidgetsStore()
    const userStore = useUserStore()
    const adType = ref(0)
    const period = ref(1)

    const defaultInfoItems = [
      {
        text: 'Balance',
        id: 'balance',
        value: formatMoney(userStore.balance / 100.0),
        icon: 'dollar-sign'
      },
      {
        text: 'Spent today',
        id: 'today',
        value: 0,
        icon: 'chart-line'
      },
      {
        text: 'Spent yesterday',
        id: 'yesterday',
        value: 0,
        icon: 'chart-line'
      },
      {
        text: 'Spent last 7 days',
        id: 'last_7_days',
        value: 0,
        icon: 'chart-line'
      },
      {
        text: 'Spent last 30 days',
        id: 'last_30_days',
        value: 0,
        icon: 'chart-line'
      },
      {
        text: 'Total spent',
        id: 'total',
        value: 0,
        icon: 'chart-line'
      }
    ]

    const infoItems = computed(() => {
      return defaultInfoItems.map((item: any) => {
        if (item.id === 'balance') {
          return {
            ...item,
            value: formatMoney(userStore.balance / 100.0)
          }
        }

        const value = widgetsStore.balanceActivity[item.id]
        if (!value) {
          return item
        }

        return {
          ...item,
          value: formatMoney(value.amount)
        }
      })
    })

    const spendingActivity = computed(() => {
      const rows = widgetsStore.spendingActivity[period.value] || []
      return userStore.hasTabFlatDeal
        ? rows
        : rows.filter((row: any) => Number(row.type_id) !== AdType.Tab)
    })

    const dashboardAdTypeLabels = computed(() =>
      userStore.hasTabFlatDeal ? FlatDealAdTypeLabels : DspAdTypeLabels
    )

    const showUserActions = computed(() => {
      return userStore.isNewDirect
    })

    const step = computed(() => {
      return userStore.onboardingStep
    })

    const isNewFlow = computed(() => {
      return userStore.isNewFlow
    })

    const fullActivity = computed(() => {
      return widgetsStore.fullActivity[adType.value] || []
    })

    const showWarning = () => {
      ElMessage.warning({
        message: 'Something went wrong',
        type: 'warning',
        showClose: true,
        duration: 5000
      })
    }

    const loadingInfoBlock = ref(false)
    const loadingSpendingBlock = ref(false)
    const loadingActivityBlock = ref(false)

    const updateSpeningdInfo = async () => {
      loadingInfoBlock.value = true
      try {
        await widgetsStore.BalanceActivity()
      } catch (err) {
        console.error(err)
        showWarning()
      } finally {
        loadingInfoBlock.value = false
      }
    }

    const updatePeriod = async (v: number) => {
      loadingSpendingBlock.value = true
      try {
        await widgetsStore.SpendingActivity(v)
      } catch (err) {
        console.error(err)
        showWarning()
      } finally {
        loadingSpendingBlock.value = false
      }
    }

    const updateAdType = async (v: number) => {
      loadingActivityBlock.value = true
      try {
        await widgetsStore.FullActivity(v)
      } catch (err) {
        showWarning()
      } finally {
        loadingActivityBlock.value = false
      }
    }

    updatePeriod(period.value)
    updateAdType(adType.value)
    updateSpeningdInfo()
    userStore.GetUserInfo(false)

    return {
      loadingInfoBlock,
      loadingSpendingBlock,
      loadingActivityBlock,
      updatePeriod,
      updateAdType,
      period,
      adType,
      infoItems,
      spendingActivity,
      dashboardAdTypeLabels,
      fullActivity,
      showUserActions,
      step,
      isNewFlow
    }
  }
})
</script>

<style lang="scss" scoped>
.period-selector {
  margin: 10px 0;
}

.box-card {
  margin-right: 10px;
}

.el-card {
  box-shadow: none;
}

.box-card.main {
  margin-top: 10px;
}

:deep(.box-card.activity .el-card__header) {
  line-height: 2.5;
}

:deep(.box-card.table .el-card__body) {
  min-height: 307px;
}

:deep(.box-card .el-card__body) {
  padding: 20px;
  padding-top: 0;
}

:deep(.el-radio-button__inner) {
  padding: 12px 8px;
}

:deep(.el-radio-button__orig-radio:checked + .el-radio-button__inner) {
  box-shadow: none !important;
}

:deep(.el-radio-button:focus:not(.is-focus):not(:active):not(.is-disabled)) {
  box-shadow: none !important;
}

.mb15 {
  margin-bottom: 15px;
}
</style>
